import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Stage, Layer } from "react-konva";
import TopMenu from "./TopMenu";
import s from "./styles";
import { Col, Row, Space, Typography } from "antd";
import Colors from "assets/Colors";
import AlertIcon from "components/icons/AlertIcon";
import Button from "components/common/Button";
import Modal from "components/common/Modal";
import { useTranslation } from "react-i18next";
import Map from "components/icons/AlertIcon";
import Pencil from "../../components/icons/Pencil";
import AddRoomModal from "components/common/AddRoomModal";
import AddTableModal from "components/common/AddTableModal";
import { useMessaging } from "context/MessagingContext";
import Decoration from "./Decorations/Decoration";
import Tables from "./Tables";
import useWindowSize from "useWindowSize";
import useRooms from "../../graphql/useRooms";
import useRoomMutations from "graphql/useRoomMutations";
import { BSON } from "realm-web";
import { useBusinessContext } from "../../BusinessApp";

const SettingsEditRooms = () => {
  const { t } = useTranslation();

  const { Text } = Typography;

  const { width } = useWindowSize();

  const [currentRoom, setCurrentRoom] = useState(null);

  const { storeId } = useBusinessContext();

  const [store, setStore] = useState(storeId);
  const { rooms, loading } = useRooms({
    storeId: store,
    limit: 1,
  });

  const { updateRoom, deleteRoom } = useRoomMutations();

  useEffect(() => {
    setStore(storeId);
  }, [storeId]);

  const [view, setView] = useState("map");

  const [addRoomModalOpen, setAddRoomModalOpen] = useState(false);
  const [addTableModalOpen, setAddTableModalOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingRoom, setEditingRoom] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [typeTable, setTypeTable] = useState(null);
  const [roomToEdit, setRoomToEdit] = useState(null);

  const [selectedId, selectShape] = React.useState(null);

  const [saving, setSaving] = useState(false);

  const { setConfirmMessage, setErrorMessage, setInfoMessage } = useMessaging();

  const handleDragEnd = (index, event, type) => {
    const updatedItems =
      type === "tables" ? currentRoom.tables : currentRoom.decorations;

    const updatedItemsArray = updatedItems.map((item, itemIndex) => {
      if (itemIndex === index) {
        return {
          ...item,
          posX: event.target.x(),
          posY: event.target.y(),
        };
      }
      return item;
    });

    const newRoom = {
      ...currentRoom,
      [type]: updatedItemsArray,
    };

    setCurrentRoom(newRoom);
  };

  const stageWidth = 1600;
  const stageHeight = 700;

  const dragBoundFunc = (pos, width, height) => {
    let newX = pos.x;
    let newY = pos.y;

    if (newX < 0) {
      newX = 0;
    } else if (newX > stageWidth - width) {
      newX = stageWidth - width;
    }

    if (newY < 0) {
      newY = 0;
    } else if (newY > stageHeight - height) {
      newY = stageHeight - height;
    }

    return {
      x: newX,
      y: newY,
    };
  };

  const handleEditTable = (type, name, capacity, roomId, tableId) => {
    const tableIndex = currentRoom.tables.findIndex(
      (table) => table._id === tableId
    );
    if (tableIndex === -1) {
      console.error("Table not found");
      return;
    }

    const selectedTable = currentRoom.tables[tableIndex];

    let updatedTable;
    const tableWidth = 74;
    const tableHeight = 74;
    if (type === "square") {
      updatedTable = {
        ...selectedTable,
        name: name || selectedTable.name,
        type: type,
        capacity: capacity || selectedTable.capacity,
        width: tableWidth,
        height: tableHeight,
      };
    } else if (type === "circle") {
      updatedTable = {
        ...selectedTable,
        name: name || selectedTable.name,
        type: type,
        capacity: capacity || selectedTable.capacity,
        width: tableWidth,
        height: tableHeight,
      };
    } else if (type === "rectangular") {
      updatedTable = {
        ...selectedTable,
        name: name || selectedTable.name,
        type: type,
        capacity: capacity || selectedTable.capacity,
        width: tableWidth * 2,
        height: tableHeight,
      };
    }

    const updatedTables = [...currentRoom.tables];
    updatedTables[tableIndex] = updatedTable;

    const updatedCurrentRoom = {
      ...currentRoom,
      tables: updatedTables,
    };

    setCurrentRoom(updatedCurrentRoom);
  };

  const handleDeleteTable = (tableId) => {
    const updatedTables = currentRoom.tables.filter(
      (table) => table._id !== tableId
    );
    const updatedCurrentRoom = {
      ...currentRoom,
      tables: updatedTables,
    };
    setCurrentRoom(updatedCurrentRoom);
    setAddTableModalOpen(false);
    setEditingTable(null);
  };

  const handleDeleteDecoration = (decorationId) => {
    const updatedDecorations = currentRoom.decorations.filter(
      (decoration) => decoration._id !== decorationId
    );
    const updatedCurrentRoom = {
      ...currentRoom,
      decorations: updatedDecorations,
    };
    setCurrentRoom(updatedCurrentRoom);
  };

  const onDeleteTable = useCallback(
    (table) => {
      setConfirmMessage({
        titleAlert: <Map size="85px" color={Colors.icon_secondary} />,
        confirm1: `${t("containers.Rooms.deleteRoom", { name: table.name })}`,
        onConfirm: () => handleDeleteTable(table._id),
      });
    },
    [currentRoom]
  );

  const onDeleteDecoration = useCallback(
    (decoration) => {
      setConfirmMessage({
        titleAlert: <Map size="85px" color={Colors.icon_secondary} />,
        confirm1: `${t("containers.Rooms.deleteDecoration")}`,
        onConfirm: () => handleDeleteDecoration(decoration._id),
      });
    },
    [currentRoom]
  );

  const onUpdateMap = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.Rooms.mapEditMessage"),
      info1: t("containers.Rooms.mapEditedMessage"),
      info2: t("containers.Rooms.mapReadyMessage"),
    });
  }, []);

  const onUpdateTableSuccess = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.Rooms.tableEditMessage"),
      info1: t("containers.Rooms.tableEditedMessage"),
      info2: t("containers.Rooms.tableFindMessage"),
    });
  }, []);

  const onAddTableSuccess = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.Rooms.tableAddedMessage"),
      info1: t("containers.Rooms.tableReadyMessage"),
      info2: t("containers.Rooms.tableFindMessage"),
    });
  }, []);

  const onUpdateRoomSuccess = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.Rooms.roomEditMessage"),
      info1: t("containers.Rooms.roomEditedMessage"),
      info2: t("containers.Rooms.roomFindMessage"),
    });
  }, []);

  const onAddRoomSuccess = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.Rooms.roomAddedMessage"),
      info1: t("containers.Rooms.roomReadyMessage"),
      info2: t("containers.Rooms.roomFindMessage"),
    });
  }, []);

  const onDeleteRoomSuccess = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.Rooms.roomDeleteMessage"),
    });
  }, []);

  const handleAddTable = (type, name, capacity, tables, decorations) => {
    const stageWidth = window.innerWidth * 0.6;
    const stageHeight = window.innerHeight;
    const tableWidth = 74;
    const tableHeight = 74;

    tables = tables || [];
    decorations = decorations || [];

    const isPositionOccupiedByTables = (x, y) => {
      return tables?.some((table) => {
        const posX = parseInt(table.posX);
        const posY = parseInt(table.posY);
        const width = parseInt(table.width);
        const height = parseInt(table.height);
        return (
          x < posX + width &&
          x + tableWidth > posX &&
          y < posY + height &&
          y + tableHeight > posY
        );
      });
    };

    const isPositionOccupiedByDecorations = (x, y) => {
      return decorations?.some((decoration) => {
        return (
          x < decoration.posX + decoration.width &&
          x + tableWidth > decoration.posX &&
          y < decoration.posY + decoration.height &&
          y + tableHeight > decoration.posY
        );
      });
    };

    let updatedTables = [...tables];
    let posX = 0;
    let posY = 0;
    let newPositionFound = false;

    while (!newPositionFound) {
      if (
        !isPositionOccupiedByTables(posX, posY) &&
        !isPositionOccupiedByDecorations(posX, posY)
      ) {
        newPositionFound = true;
      } else {
        posX += tableWidth + 10;
        if (posX + tableWidth > stageWidth) {
          posX = 0;
          posY += tableHeight + 10;
        }
        if (posY + tableHeight > stageHeight) {
          posX = stageWidth - tableWidth;
          posY = stageHeight - tableHeight;
          newPositionFound = true;
        }
      }
    }

    let newTable;
    if (type === "square") {
      newTable = {
        _id: new BSON.ObjectId(),
        name: name || `Mesa #${currentRoom.tables.length + 1}`,
        type: type,
        color: null,
        capacity: capacity || 0,
        posX: posX,
        posY: posY,
        width: tableWidth,
        height: tableHeight,
      };
    } else if (type === "circle") {
      newTable = {
        _id: new BSON.ObjectId(),
        name: name || `Mesa #${currentRoom.tables.length + 1}`,
        type: type,
        color: null,
        capacity: capacity || 0,
        width: tableWidth,
        height: tableHeight,
        posX: posX,
        posY: posY,
      };
    } else if (type === "rectangular") {
      newTable = {
        _id: new BSON.ObjectId(),
        name: name || `Mesa #${currentRoom.tables.length + 1}`,
        type: type,
        color: null,
        capacity: capacity || 0,
        posX: posX,
        posY: posY,
        width: tableWidth * 2,
        height: tableHeight,
      };
    }

    updatedTables.push(newTable);

    setCurrentRoom({ ...currentRoom, tables: updatedTables });
  };

  const handleAddDecoration = (decorations, tables) => {
    const stageWidth = 1600;
    const stageHeight = 700;
    const decorationWidth = 400;
    const decorationHeight = 40;
    const marginBetweenDecorations = 10;

    let updatedDecorations = [];

    if (decorations) {
      updatedDecorations = [...decorations];
    }

    if (!tables) {
      tables = [];
    }

    const isPositionOccupiedByTables = (x, y) => {
      return tables?.some((table) => {
        const posX = parseInt(table.posX);
        const posY = parseInt(table.posY);
        return (
          x < posX + parseInt(table.width) &&
          x + decorationWidth + marginBetweenDecorations > posX &&
          y < posY + parseInt(table.height) &&
          y + decorationHeight + marginBetweenDecorations > posY
        );
      });
    };

    const isPositionOccupiedByDecorations = (x, y) => {
      return updatedDecorations.some((decoration) => {
        return (
          x < decoration.posX + decoration.width + marginBetweenDecorations &&
          x + decorationWidth + marginBetweenDecorations > decoration.posX &&
          y < decoration.posY + decoration.height + marginBetweenDecorations &&
          y + decorationHeight + marginBetweenDecorations > decoration.posY
        );
      });
    };

    let posX = 0;
    let posY = 0;
    let spaceAvailable = true;

    while (
      isPositionOccupiedByTables(posX, posY) ||
      isPositionOccupiedByDecorations(posX, posY) ||
      posX + decorationWidth + marginBetweenDecorations > stageWidth ||
      posY + decorationHeight + marginBetweenDecorations > stageHeight
    ) {
      posX += decorationWidth + marginBetweenDecorations;
      if (posX + decorationWidth + marginBetweenDecorations > stageWidth) {
        posX = 0;
        posY += decorationHeight + marginBetweenDecorations;
      }
      if (posY + decorationHeight + marginBetweenDecorations > stageHeight) {
        spaceAvailable = false;
        break;
      }
    }

    if (spaceAvailable) {
      const newDecoration = {
        _id: new BSON.ObjectId(),
        name: "Muro",
        type: "wall",
        color: null,
        posX: posX,
        posY: posY,
        width: decorationWidth,
        height: decorationHeight,
      };

      updatedDecorations.push(newDecoration);

      setCurrentRoom({ ...currentRoom, decorations: updatedDecorations });
    } else {
      return;
    }
  };

  const editRoom = () => {
    setAddRoomModalOpen(true);
    setEditingRoom(true);
    setRoomToEdit(currentRoom);
  };

  const handleDeleteRoom = (room) => {
    deleteRoom(room)
      .then(() => {
        onDeleteRoomSuccess();
        setCurrentRoom(null);
        setAddRoomModalOpen(false);
        setRoomToEdit(null);
      })
      .catch((err) => {
        setErrorMessage({ error: err });
      });
  };

  const onDeleteRoom = useCallback(
    (room) => {
      setConfirmMessage({
        titleAlert: <Map size="85px" color={Colors.icon_secondary} />,
        confirm1: `${t("containers.Rooms.deleteRoom", { name: room.name })}`,
        confirm2: t("containers.Rooms.deleteRoomUndone"),
        onConfirm: () => handleDeleteRoom(room),
      });
    },
    [currentRoom]
  );

  const handleSave = () => {
    const roomToEdit = rooms.find((room) => room._id === currentRoom._id);

    const { __typename, ...cleanRoom } = currentRoom;

    cleanRoom.name = roomToEdit.name;
    cleanRoom.priceLists = roomToEdit.priceLists;

    const tables = cleanRoom.tables?.map((table) => {
      const { __typename, ...cleanTable } = table;
      cleanTable.owner_id = currentRoom._id;
      cleanTable.capacity = parseInt(cleanTable.capacity).toString();
      cleanTable.posX = parseInt(cleanTable.posX).toString();
      cleanTable.posY = parseInt(cleanTable.posY).toString();
      cleanTable.width = parseInt(cleanTable.width).toString();
      cleanTable.height = parseInt(cleanTable.height).toString();
      return cleanTable;
    });

    const decorations = cleanRoom.decorations?.map((decoration) => {
      const { __typename, ...cleanDecoration } = decoration;
      cleanDecoration.owner_id = currentRoom._id;
      cleanDecoration.posX = parseInt(cleanDecoration.posX).toString();
      cleanDecoration.posY = parseInt(cleanDecoration.posY).toString();
      cleanDecoration.width = parseInt(cleanDecoration.width).toString();
      cleanDecoration.height = parseInt(cleanDecoration.height).toString();
      return cleanDecoration;
    });

    const modifiedData = {
      ...cleanRoom,
      tables,
      decorations,
    };

    setSaving(true);

    updateRoom(roomToEdit, modifiedData)
      .then(() => {
        onUpdateMap();
        setEditing(false);
        setSaving(false);
      })
      .catch((err) => {
        setErrorMessage({ error: err });
        setSaving(false);
      });
  };

  const handleCancel = () => {
    setCurrentRoom(rooms.find((room) => room._id === currentRoom._id));
  };

  const isEmpty =
    (currentRoom?.tables?.length || 0) !== 0
      ? false
      : true && (currentRoom?.decorations?.length || 0) !== 0
      ? false
      : true;

  const stylesComponents = useMemo(
    () => ({
      width: "20px",
      height: "20px",
      border: `none`,
      background: "transparent",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "absolute",
      top: "5px",
      right: "5px",
    }),
    []
  );

  const checkDeselect = (e) => {
    const clickedOnEmpty = e.target === e.target.getStage();
    if (clickedOnEmpty) {
      selectShape(null);
    }
  };

  return (
    <s.PageContainer>
      <TopMenu
        rooms={rooms}
        store={store}
        setStore={setStore}
        currentRoom={currentRoom}
        setCurrentRoom={setCurrentRoom}
        view={view}
        setView={setView}
        editing={editing}
        setEditing={setEditing}
        addRoom={() => {
          setAddRoomModalOpen(true);
        }}
        editRoom={editRoom}
        roomToEdit={roomToEdit}
        // setRoomToEdit={setRoomToEdit}
        handleCancel={handleCancel}
        handleSave={handleSave}
        loading={loading}
        saving={saving}
        selectShape={selectShape}
      />
      {currentRoom?.virtual ? (
        <Row
          style={{
            width: "97%",
            margin: "auto auto",
          }}
        >
          <s.NoItemsContainer>
            <s.NoItemsMessageCenter>
              <AlertIcon style={{ display: "block", marginBottom: "70px" }} />
              <Space
                direction="vertical"
                style={{ textAlign: "center", marginTop: "20px" }}
              >
                <Text type="secondary">
                  {t("containers.Rooms.virtualRoomPlaceHolder")}
                </Text>
                <Text type="secondary">
                  {t("containers.Rooms.virtualRoomPlaceHolder2")}
                </Text>
              </Space>
            </s.NoItemsMessageCenter>
          </s.NoItemsContainer>
        </Row>
      ) : (
        <Row
          style={{
            width: "97%",
            margin: "auto",
            marginLeft: "1.5%",
            opacity: editing ? 1 : 0.5,
          }}
        >
          <Col lg={17} md={14} xs={24}>
            {isEmpty ? (
              <s.NoItemsContainer>
                <s.NoItemsMessageCenter>
                  <AlertIcon
                    style={{ display: "block", marginBottom: "7px" }}
                  />
                  {t("general.noData")}
                </s.NoItemsMessageCenter>
              </s.NoItemsContainer>
            ) : view === "map" ? (
              <Stage
                key={currentRoom?._id}
                width={stageWidth}
                height={stageHeight}
                style={{
                  background: `${Colors.GREY3}`,
                  width: "100%",
                  padding: "2px",
                  height: "600px",
                  overflow: "scroll",
                }}
                onMouseDown={checkDeselect}
                onTouchStart={checkDeselect}
              >
                <Layer>
                  {currentRoom.decorations?.map((rect, i) => {
                    return (
                      <Decoration
                        key={i}
                        shapeProps={rect}
                        isSelected={rect._id === selectedId}
                        onSelect={() => {
                          selectShape(rect._id);
                        }}
                        onChange={(newAttrs) => {
                          const rects = currentRoom.decorations.slice();
                          rects[i] = newAttrs;
                          setCurrentRoom({
                            ...currentRoom,
                            decorations: rects,
                          });
                        }}
                        index={i}
                        handleDragEnd={handleDragEnd}
                        dragBoundFunc={dragBoundFunc}
                        onDeleteDecoration={onDeleteDecoration}
                        editing={editing}
                      />
                    );
                  })}

                  {currentRoom.tables?.map((rect, i) => {
                    return (
                      <Tables
                        key={i}
                        shapeProps={rect}
                        isSelected={rect._id === selectedId}
                        onSelect={() => {
                          selectShape(rect._id);
                        }}
                        onChange={(newAttrs) => {
                          const rects = currentRoom.tables.slice();
                          rects[i] = newAttrs;
                          setCurrentRoom({
                            ...currentRoom,
                            tables: rects,
                          });
                        }}
                        index={i}
                        handleDragEnd={handleDragEnd}
                        dragBoundFunc={dragBoundFunc}
                        editing={editing}
                        setAddTableModalOpen={setAddTableModalOpen}
                        setEditingTable={setEditingTable}
                      />
                    );
                  })}
                </Layer>
              </Stage>
            ) : (
              <s.ContainerViewIcons editing={editing}>
                {(currentRoom?.tables?.length || 0) === 0 ? (
                  <s.NoItemsContainer>
                    <s.NoItemsMessageCenter>
                      <AlertIcon
                        style={{ display: "block", marginBottom: "7px" }}
                      />
                      {t("general.noData")}
                    </s.NoItemsMessageCenter>
                  </s.NoItemsContainer>
                ) : (
                  currentRoom?.tables?.map((table) => {
                    if (table.type === "square") {
                      return (
                        <s.squareTable
                          key={table.id}
                          onClick={() => [
                            setAddTableModalOpen(true),
                            setTypeTable("square"),
                            setEditingTable(table),
                          ]}
                        >
                          <Button
                            disabled={!editing}
                            style={stylesComponents}
                            onClick={() => {
                              setAddTableModalOpen(true);
                              setEditingTable(table);
                            }}
                            icon={
                              editing ? (
                                <Pencil
                                  size={15}
                                  color={Colors.background_dividers}
                                />
                              ) : null
                            }
                          ></Button>
                          <s.TextSelectorComponentTitle>
                            {table.name}
                          </s.TextSelectorComponentTitle>
                          <s.TextSelectorComponentTitle>
                            {`(${table.capacity}p)`}
                          </s.TextSelectorComponentTitle>
                        </s.squareTable>
                      );
                    } else if (table.type === "circle") {
                      return (
                        <s.roundTable
                          key={table.id}
                          onClick={() => [
                            setAddTableModalOpen(true),
                            setTypeTable("circle"),
                          ]}
                        >
                          <Button
                            disabled={!editing}
                            style={{
                              ...stylesComponents,
                              left: "50%",
                              transform: "translateX(-50%)",
                            }}
                            onClick={() => {
                              setAddTableModalOpen(true);
                              setEditingTable(table);
                            }}
                            icon={
                              editing ? (
                                <Pencil
                                  size={15}
                                  color={Colors.background_dividers}
                                />
                              ) : null
                            }
                          ></Button>
                          <s.TextSelectorComponentTitle>
                            {table.name}
                          </s.TextSelectorComponentTitle>
                          <s.TextSelectorComponentTitle>
                            {`(${table.capacity}p)`}
                          </s.TextSelectorComponentTitle>
                        </s.roundTable>
                      );
                    } else if (table.type === "rectangular") {
                      return (
                        <s.rectangleTable
                          width="200px"
                          key={table.id}
                          onClick={() => [
                            setAddTableModalOpen(true),
                            setTypeTable("rectangular"),
                          ]}
                        >
                          <Button
                            disabled={!editing}
                            style={stylesComponents}
                            onClick={() => {
                              setAddTableModalOpen(true);
                              setEditingTable(table);
                            }}
                            icon={
                              editing ? (
                                <Pencil
                                  size={15}
                                  color={Colors.background_dividers}
                                />
                              ) : null
                            }
                          ></Button>
                          <s.TextSelectorComponentTitle>
                            {table.name}
                          </s.TextSelectorComponentTitle>
                          <s.TextSelectorComponentTitle>
                            {`(${table.capacity}p)`}
                          </s.TextSelectorComponentTitle>
                        </s.rectangleTable>
                      );
                    }
                  })
                )}
              </s.ContainerViewIcons>
            )}
          </Col>

          <s.containerTables
            onClick={() => {
              selectedId && selectShape(null);
            }}
            editing={editing ? "true" : undefined}
            lg={7}
            md={10}
            xs={24}
          >
            <s.TextSelectorComponentTitle
              style={{ marginTop: "0px", marginBottom: "20px" }}
            >
              {t("containers.Rooms.addTables")}
            </s.TextSelectorComponentTitle>

            <Row gutter={[30, 30]}>
              <Col xs={12} md={12} span={6}>
                <s.squareTable
                  onClick={
                    () => [setAddTableModalOpen(true), setTypeTable("square")]
                    // handleAddTable("square")
                  }
                >
                  <s.TextSelectorComponentTitle>
                    {t("containers.Rooms.xPeople")}
                  </s.TextSelectorComponentTitle>
                </s.squareTable>
                <s.TextSelectorComponent>
                  {t("containers.Rooms.squareTable")}
                </s.TextSelectorComponent>
              </Col>
              <Col
                xs={12}
                md={12}
                span={6}
                style={{
                  display: "flex",
                  flexDirection: "colum",
                  justifyContent: width > 768 ? "end" : "flex-start",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <s.roundTable
                    onClick={() => {
                      setAddTableModalOpen(true), setTypeTable("circle");
                    }}
                  >
                    <s.TextSelectorComponentTitle>
                      {t("containers.Rooms.xPeople")}
                    </s.TextSelectorComponentTitle>
                  </s.roundTable>
                  <s.TextSelectorComponent>
                    {" "}
                    {t("containers.Rooms.roundTable")}
                  </s.TextSelectorComponent>
                </div>
              </Col>
              <Col xs={12} md={24} span={6}>
                <s.rectangleTable
                  onClick={() => {
                    setAddTableModalOpen(true), setTypeTable("rectangular");
                  }}
                >
                  <s.TextSelectorComponentTitle>
                    {t("containers.Rooms.xPeople")}
                  </s.TextSelectorComponentTitle>
                </s.rectangleTable>
                <s.TextSelectorComponent>
                  {t("containers.Rooms.rectangularTable")}
                </s.TextSelectorComponent>
              </Col>
              {view === "map" && (
                <Col
                  xs={12}
                  md={24}
                  span={6}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "end",
                  }}
                >
                  <s.wall
                    onClick={() =>
                      handleAddDecoration(
                        currentRoom.decorations,
                        currentRoom.tables
                      )
                    }
                  ></s.wall>
                  <s.TextSelectorComponent>
                    {" "}
                    {t("containers.Rooms.wall")}
                  </s.TextSelectorComponent>
                </Col>
              )}
            </Row>
          </s.containerTables>
        </Row>
      )}

      <Modal
        width={642}
        onCancel={() => {
          console.log("close");
          setRoomToEdit(null);
          setAddRoomModalOpen(false);
          setEditingRoom(false);
        }}
        visible={addRoomModalOpen}
        destroyOnClose={true}
      >
        {addRoomModalOpen && (
          <AddRoomModal
            close={() => {
              setRoomToEdit(null);
              setAddRoomModalOpen(false);
              setEditingRoom(false);
            }}
            setCurrentRoom={setCurrentRoom}
            store={store}
            rooms={rooms}
            onUpdateRoomSuccess={onUpdateRoomSuccess}
            onAddRoomSuccess={onAddRoomSuccess}
            setErrorMessage={setErrorMessage}
            handleDeleteRoom={onDeleteRoom}
            room={roomToEdit}
          />
        )}
      </Modal>

      <Modal
        width={642}
        onCancel={() => {
          setAddTableModalOpen(false);
          setEditingTable(null);
        }}
        visible={addTableModalOpen}
      >
        {addTableModalOpen && (
          <AddTableModal
            close={() => {
              setAddTableModalOpen(false);
              setEditingTable(null);
            }}
            handleDeleteRoom={handleDeleteRoom}
            room={editingRoom && roomToEdit}
            typeTable={typeTable}
            handleAddTable={handleAddTable}
            handleEditTable={handleEditTable}
            setErrorMessage={setErrorMessage}
            editingTable={editingTable}
            handleDeleteTable={onDeleteTable}
            currentRoom={currentRoom}
            onUpdateTableSuccess={onUpdateTableSuccess}
            onAddTableSuccess={onAddTableSuccess}
          />
        )}
      </Modal>
    </s.PageContainer>
  );
};

export default SettingsEditRooms;
